import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { getRoomTypeDetail } from '@/API/apiService';
import { useForm, FormProvider } from 'react-hook-form';
import { MonthlyReservationTableBody } from '@/components/monthly/ReservationTableBody';
import { RoomTypeCard } from '@/components/monthly/RoomTypeCard';
import { NotFound } from '@/pages/errors/NotFound';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

import { EstimateForm } from '@/components/monthly/EstimateForm';
import { ContractInfoForm } from './ApplyForms/ContractInfoForm';
import { WorkInfoForm } from './ApplyForms/WorkInfoForm';
import { ResidentInfoForm } from './ApplyForms/ResidentInfoForm';
import { EmergencyContactForm } from './ApplyForms/EmergencyContactForm';
import { FormSection } from './ApplyForms/FormSection';

import { IDUploadForm, IDUploadFormRef } from './ApplyForms/IDUploadForm';

// フローチャート画像のインポート
import FlowEN from 'src/components/monthly/ApplyForms/img/FlowEN.png';
import FlowJP from 'src/components/monthly/ApplyForms/img/FlowJP.png';

/**
 * 見積り関連の型
 */
export interface EstimateData {
  fromDate: string;
  toDate: string;
  guests: string;
  period?: string;
}

/**
 * 契約者情報
 */
export interface ContractData {
  firstName: string;
  lastName: string;
  firstNameKana: string;
  lastNameKana: string;
  country: string;
  address: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  gender: string;
  email: string;
  phoneNumber: string;
}

/**
 * 勤務先情報
 */
export interface WorkData {
  companyName: string;
  department?: string;
  companyAddress?: string;
  companyPhone?: string;
  industry?: string;
  jobType?: string;
  position?: string;
  annualIncome?: string;
}

/**
 * 入居者情報
 */
export interface ResidentData {
  firstName: string;
  lastName: string;
  firstNameKana: string;
  lastNameKana: string;
  relation: string;
  country: string;
  address: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  ResidentGender: string;
  email: string;
  phoneNumber: string;
}

/**
 * 緊急連絡先
 */
export interface EmergencyData {
  firstName: string;
  lastName: string;
  firstNameKana?: string;
  lastNameKana?: string;
  relation?: string;
  address?: string;
  birthYear?: string;
  birthMonth?: string;
  birthDay?: string;
  EmergencyGender?: string;
  phoneNumber?: string;
}

/**
 * 身分証明書アップロード
 */
export interface IdUploadData {
  idType: string;
  frontFile: File | null;
  backFile: File | null;
}

/**
 * 全体フォームデータ
 */
export interface ApplyFormData {
  estimateData: EstimateData;
  contractData: ContractData;
  workData: WorkData;
  residentData: ResidentData;
  emergencyData: EmergencyData;
  idUploadData: IdUploadData;
}

/**
 * 親からのProps
 */
export interface ApplyFormComponent1Props {
  onNext: (data: any) => void;
}

export const ApplyFormComponent1: React.FC<ApplyFormComponent1Props> = ({
  onNext,
}) => {
  const { property_id, room_id } = useParams();

  // URL から言語を判定
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const [isLoading, setIsLoading] = useState(true);
  const [roomData, setRoomData] = useState<any | null | undefined>(undefined);
  const [monthlyRentals, setMonthlyRentals] = useState<any[]>([]);
  const [reservationError, setReservationError] = useState<string>('');

  // (A) データ取得
  useEffect(() => {
    const fetchRoomData = async () => {
      try {
        const result = await getRoomTypeDetail(
          String(property_id),
          String(room_id)
        );
        const data = result?.data;
        setRoomData(data || null);
        setMonthlyRentals(data?.monthlyRentals || []);
      } catch (error) {
        console.error('Error fetching room detail:', error);
        setRoomData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRoomData();
  }, [property_id, room_id]);

  // (B) React Hook Form のセットアップ
  const methods = useForm<ApplyFormData>({
    defaultValues: loadFromLocalStorage(),
    mode: 'onChange',
    shouldUnregister: false,
  });
  const { handleSubmit, watch } = methods;
  const allValues = watch();

  useEffect(() => {
    saveToLocalStorage(allValues);
  }, [allValues]);

  // 予約エラーがあれば localStorage から取得し state にセット（取得後クリア）
  useEffect(() => {
    const err = localStorage.getItem('reservationError');
    if (err) {
      setReservationError(err);
      localStorage.removeItem('reservationError');
    }
  }, []);

  function loadFromLocalStorage(): ApplyFormData {
    const defaults: ApplyFormData = {
      estimateData: {
        fromDate: '',
        toDate: '',
        guests: '1',
      },
      contractData: {
        firstName: '',
        lastName: '',
        firstNameKana: '',
        lastNameKana: '',
        country: '',
        address: '',
        birthYear: '',
        birthMonth: '',
        birthDay: '',
        gender: '',
        email: '',
        phoneNumber: '',
      },
      workData: {
        companyName: '',
        department: '',
        companyAddress: '',
        companyPhone: '',
        industry: '',
        jobType: '',
        position: '',
        annualIncome: '',
      },
      residentData: {
        firstName: '',
        lastName: '',
        firstNameKana: '',
        lastNameKana: '',
        relation: '',
        country: '',
        address: '',
        birthYear: '',
        birthMonth: '',
        birthDay: '',
        ResidentGender: '',
        email: '',
        phoneNumber: '',
      },
      emergencyData: {
        firstName: '',
        lastName: '',
        firstNameKana: '',
        lastNameKana: '',
        relation: '',
        address: '',
        birthYear: '',
        birthMonth: '',
        birthDay: '',
        EmergencyGender: '',
        phoneNumber: '',
      },
      idUploadData: {
        idType: '',
        frontFile: null,
        backFile: null,
      },
    };

    try {
      const stored = localStorage.getItem('applyForm_textData');
      if (!stored) return defaults;
      const parsed = JSON.parse(stored);
      return {
        ...defaults,
        ...parsed,
        idUploadData: {
          ...defaults.idUploadData,
          ...(parsed.idUploadData || {}),
          frontFile: null,
          backFile: null,
        },
      };
    } catch {
      return defaults;
    }
  }

  function saveToLocalStorage(data: ApplyFormData) {
    const { frontFile, backFile, ...rest } = data.idUploadData;
    const textOnly = {
      ...data,
      idUploadData: {
        ...rest,
      },
    };
    localStorage.setItem('applyForm_textData', JSON.stringify(textOnly));
  }

  // (D) IDUploadForm の ref
  const idUploadFormRef = useRef<IDUploadFormRef>(null);

  // (E) onSubmit => ファイル必須チェックを実施
  const onSubmit = (data: ApplyFormData) => {
    let hasError = false;
    const { idType, frontFile, backFile } = data.idUploadData;
    if (!frontFile) {
      idUploadFormRef.current?.setFrontError(
        useEnglish ? 'No file selected' : 'ファイルが選択されていません'
      );
      hasError = true;
    }
    if (idType.includes('(表・裏)') && !backFile) {
      idUploadFormRef.current?.setBackError(
        useEnglish ? 'No file selected' : 'ファイルが選択されていません'
      );
      hasError = true;
    }
    if (!idType) {
      hasError = true;
    }
    if (hasError) {
      return;
    }
    // (F) ここで roomData から取得した物件情報をマージします。
    const merged = {
      ...data,
      roomType: roomData?.room_type_name || '',
      roomType_en: roomData?.room_type_name_en || '',
      propertyName: roomData?.property_name || '',
      propertyName_en: roomData?.property_name_en || '',
      imageUrl: roomData?.image_url?.[0] || '',
      capacity: roomData?.capacity ? `${roomData.capacity} people` : '',
      // roomData に room_area がなければ monthlyRentals の先頭要素の room_area を利用
      room_area: roomData?.room_area || (monthlyRentals[0]?.room_area ?? ''),
      description: roomData?.description || '',
      description_en: roomData?.description_en || '',
      price: roomData?.prices?.monthly_price ?? 0,
    };
    onNext(merged);
  };

  if (isLoading) return <LoadingSpinner />;
  if (roomData === null) return <NotFound />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="bg-whiteYellow px-4">
        {/* 上部ロゴ的リンク部分 */}
        <Link to={`/${langSegment}/monthly`} className="block">
          The (Apartment)
          <div>Hotel</div>
        </Link>

        <div className="max-w-[610px] w-full mx-auto">
          <div className="text-2xl py-12">Reservation</div>

          {/* 部屋情報 */}
          <div className="mb-5">
            {roomData && (
              <RoomTypeCard
                propertyName={roomData.property_name}
                propertyName_en={roomData.property_name_en}
                roomType={roomData.room_type_name}
                roomType_en={roomData.room_type_name_en}
                imageUrl={roomData.image_url?.[0] ?? ''}
                capacity={`${roomData.capacity} people`}
                area={`${roomData.room_area}m²`}
                area_en={roomData.area_name_en}
                description={roomData.description}
                description_en={roomData.description_en}
                useEnglish={useEnglish}
                roomArea={roomData.room_area}
              />
            )}
          </div>

          {/* 在庫状況 */}
          <MonthlyReservationTableBody monthlyRentals={monthlyRentals} />

          {/* 予約エラーのメッセージ */}
          {reservationError && (
            <div className="mt-4 text-red-500 text-sm">{reservationError}</div>
          )}

          {/* 見積フォーム */}
          <EstimateForm
            price={roomData?.prices?.monthly_price ?? 0}
            monthlyRentals={monthlyRentals}
            capacity={roomData?.capacity || 1}
          />

          {/* ここから追加：契約者情報の上にフローチャート画像と見出しを表示 */}
          <div className="my-4">
            <span className="font-bold text-md mb-2">
              {useEnglish ? 'Move-in Process' : 'ご入居までの流れ'}
            </span>
            <div className="flex justify-center my-4 md:mx-14">
              <img
                src={useEnglish ? FlowEN : FlowJP}
                alt="Flow Chart"
                className="max-w-full"
              />
            </div>
          </div>

          {/* 契約者情報 */}
          <FormSection
            title={useEnglish ? 'Contractor Information' : 'ご契約者様の情報'}
          >
            <ContractInfoForm />
          </FormSection>

          {/* ご勤務先 */}
          <FormSection
            title={useEnglish ? 'Work Information' : 'ご勤務先の情報'}
          >
            <WorkInfoForm />
          </FormSection>

          {/* ご入居される方 */}
          <FormSection
            title={useEnglish ? 'Resident Information' : 'ご入居される方の情報'}
          >
            <ResidentInfoForm />
          </FormSection>

          {/* 緊急連絡先 */}
          <FormSection title={useEnglish ? 'Emergency Contact' : '緊急連絡先'}>
            <EmergencyContactForm />
          </FormSection>

          {/* 身分証明書 */}
          <FormSection
            title={useEnglish ? 'ID Upload' : '身分証明書のアップロード'}
          >
            <IDUploadForm ref={idUploadFormRef} />
          </FormSection>

          <div className="flex justify-center">
            <button
              type="submit"
              className="mt-4 px-2 py-2 text-white bg-black mb-20 w-[144px]"
            >
              {useEnglish ? 'Reservation' : '予約する'}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default ApplyFormComponent1;
